import {
	AspectMaintainer, Button, ButtonTypes, RangeSlider, Select, Switch, InteractiveSvg
} from "@clintonelec/react-storybook";
import { irisOptions } from "Data/Objects/Camera";
import { produce } from "immer";
import type { ImageSettingsState } from "Interfaces";
import { type Dispatch, memo, type SetStateAction } from "react";
import type { Point } from "@clintonelec/react-storybook";

type ZoomFocusProps = {
	localSettings: ImageSettingsState;
	setLocalSettings: Dispatch<SetStateAction<ImageSettingsState>>;
};

function ZoomFocus(props: ZoomFocusProps) {
	const { localSettings, setLocalSettings } = props;
	const { zoomFocusArea, zoom, focus, iris, dayNightFocusShift } = localSettings.zoomFocus;

	const handleFocusedAreaChange = (area: [ Point, Point, Point, Point ]) => {
		setLocalSettings(produce(localSettings, draft => {
			draft.zoomFocus.zoomFocusArea = area;
		}));
	};

	const handleDefaultAreaClick = () => {
		setLocalSettings(produce(localSettings, draft => {
			draft.zoomFocus.zoomFocusArea = [
				{ x: 760, y: 340 },
				{ x: 1160, y: 340 },
				{ x: 1160, y: 740 },
				{ x: 760, y: 740 }
			];
		}));
	};

	const handleZoomChange = (value: number | number[]) => {
		if (Array.isArray(value)) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.zoomFocus.zoom = value;
		}));
	};

	const handleFocusChange = (value: number | number[]) => {
		if (Array.isArray(value)) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.zoomFocus.focus = value;
		}));
	};

	const handleIrisSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.zoomFocus.iris = value;
		}));
	};

	const handleDayNightFocusShiftChecked = (checked: boolean) => {
		setLocalSettings(produce(localSettings, draft => {
			draft.zoomFocus.dayNightFocusShift = checked;
		}));
	};

	return (
		<div className="two-column-card-content">
			<div className="left-content">
				<AspectMaintainer>
					<div className="camera-snapshot">
						<InteractiveSvg>
							<InteractiveSvg.Rectangle
								points={ zoomFocusArea }
								onChange={ handleFocusedAreaChange } />
						</InteractiveSvg>
					</div>
				</AspectMaintainer>
				<div className="form-row">
					<div className="span-button-wrapper">
						<span>Draw a rectangle to define the focus area.</span>
						<Button
							htmlType="button"
							onClick={ handleDefaultAreaClick }
							type={ ButtonTypes.TERTIARY }
						>
							Default Area
						</Button>
					</div>
				</div>
			</div>
			<div className="right-content">
				<div className="form-row">
					<span>Zoom</span>
					<div className="form-slider-wrapper">
						<RangeSlider
							hideInputCharMessage
							inputDisplay
							max={ 1335 }
							min={ 0 }
							onChange={ handleZoomChange }
							value={ zoom }
						/>
					</div>
				</div>
				<div className="form-row">
					<span>Focus</span>
					<div className="form-slider-wrapper">
						<RangeSlider
							hideInputCharMessage
							inputDisplay
							max={ 3018 }
							min={ 0 }
							onChange={ handleFocusChange }
							value={ focus }
						/>
					</div>
				</div>
				<div className="form-row">
					<span>Iris</span>
					<Select
						allowClear={ false }
						className="form-select"
						onSelect={ handleIrisSelect }
						options={ irisOptions }
						value={ iris }
					/>
				</div>
				<div className="form-row">
					<span>Day/Night Focus Shift</span>
					<div className="form-switch-wrapper">
						<Switch
							checked={ dayNightFocusShift }
							onChange={ handleDayNightFocusShiftChecked }
						/>
					</div>
				</div>
				<div className="form-row">&nbsp;</div>
				<div className="form-row">
					<span>Refocus Lens</span>
					<Button className="form-button" htmlType="button" type={ ButtonTypes.TERTIARY }>Refocus</Button>
				</div>
				<div className="form-row">
					<span>Lens Reset</span>
					<Button className="form-button" htmlType="button" type={ ButtonTypes.TERTIARY }>Home</Button>
				</div>
			</div>
		</div>
	);
}

export default memo(ZoomFocus);
